import { useState, useEffect, useCallback, useRef } from 'react';
import { pagamentosApi, planosApi, Plano } from '../api/api';

interface Props {
  nomeCompleto: string;
  subPage: 'wall' | 'sucesso' | 'falha' | 'pendente';
  onSubscriptionActive: (expiracao: string) => void;
  onLogout: () => void;
  onVoltar?: () => void;
}

type Method = 'pix' | 'credit_card';

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-70" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ─── Sub-páginas ──────────────────────────────────────────────────────────────

function SuccessPage({ onSubscriptionActive }: { onSubscriptionActive: (e: string) => void }) {
  const [polling, setPolling] = useState(true);
  const [msg, setMsg] = useState('Confirmando seu pagamento...');

  const check = useCallback(async () => {
    try {
      const { data } = await pagamentosApi.verificarAssinatura();
      if (data.ativo && data.expiracao) onSubscriptionActive(data.expiracao);
    } catch {}
  }, [onSubscriptionActive]);

  useEffect(() => {
    check();
    let n = 0;
    const iv = setInterval(async () => {
      if (++n > 20) { clearInterval(iv); setPolling(false); setMsg('Ainda processando. Aguarde ou faça login novamente em instantes.'); return; }
      await check();
    }, 3000);
    return () => clearInterval(iv);
  }, [check]);

  return (
    <Screen>
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-6">
          {polling ? <Spinner className="w-8 h-8 text-emerald-500" /> : (
            <svg className="w-9 h-9 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagamento confirmado!</h1>
        <p className="text-sm text-gray-500">{msg}</p>
      </div>
    </Screen>
  );
}

function FailurePage() {
  return (
    <Screen>
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center mx-auto mb-6">
          <svg className="w-9 h-9 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagamento recusado</h1>
        <p className="text-sm text-gray-500 mb-8">Verifique os dados e tente novamente com outro método.</p>
        <button onClick={() => window.location.href = '/'} className="w-full h-12 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors">
          Tentar novamente
        </button>
      </div>
    </Screen>
  );
}

function PendingPage() {
  return (
    <Screen>
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-yellow-50 border-2 border-[rgba(245,158,11,0.3)] flex items-center justify-center mx-auto mb-6">
          <svg className="w-9 h-9 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Aguardando confirmação</h1>
        <p className="text-sm text-gray-500 mb-8">Seu pagamento está sendo processado. O acesso será liberado automaticamente.</p>
        <button onClick={() => window.location.href = '/'} className="w-full h-12 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:border-gray-300 transition-colors">
          Verificar agora
        </button>
      </div>
    </Screen>
  );
}

// ─── Layout wrapper ───────────────────────────────────────────────────────────

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl shadow-sm border p-8 bg-white border border-gray-200">{children}</div>
    </div>
  );
}

// ─── QR Code PIX ─────────────────────────────────────────────────────────────

type PixStatus = 'pending' | 'approved' | 'rejected' | 'rejected_high_risk' | 'expired' | 'cancelled';

function PixScreen({
  qrCode, qrCodeBase64, paymentId, nomeProduto, onApproved, onBack,
}: {
  qrCode: string; qrCodeBase64: string; paymentId: string;
  nomeProduto: string; onApproved: () => void; onBack: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [pixStatus, setPixStatus] = useState<PixStatus>('pending');
  const [mensagemErro, setMensagemErro] = useState('');

  const handleCopy = () => {
    navigator.clipboard.writeText(qrCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const { data } = await pagamentosApi.consultarStatus(paymentId);
        if (data.status === 'approved') {
          setPixStatus('approved');
          clearInterval(iv);
          setTimeout(onApproved, 800);
        } else if (['rejected', 'cancelled', 'refunded', 'charged_back'].includes(data.status)) {
          // Usa statusInterno normalizado retornado pelo backend (evita tratar tudo como "expirado")
          const statusInterno = data.statusInterno as PixStatus;
          const finalStatus: PixStatus =
            statusInterno === 'rejected_high_risk' ? 'rejected_high_risk' :
            statusInterno === 'expired' ? 'expired' :
            statusInterno === 'cancelled' ? 'cancelled' :
            'rejected';
          setPixStatus(finalStatus);
          setMensagemErro(data.mensagemAmigavel || '');
          clearInterval(iv);
        }
      } catch {}
    }, 4000);
    return () => clearInterval(iv);
  }, [paymentId, onApproved]);

  if (pixStatus === 'approved') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-gray-900 font-semibold">PIX aprovado! Liberando acesso...</p>
      </div>
    );
  }

  if (pixStatus === 'rejected_high_risk') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-orange-50 border-2 border-[rgba(249,115,22,0.3)] flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <p className="text-gray-900 font-semibold mb-2">Pagamento recusado por análise de risco</p>
        <p className="text-sm text-gray-500 mb-4">{mensagemErro || 'O Mercado Pago recusou este pagamento por segurança. Tente outro método de pagamento.'}</p>
        <button onClick={onBack} className="text-sm text-emerald-700 hover:text-emerald-700 font-medium transition-colors">
          ← Tentar com outro método
        </button>
      </div>
    );
  }

  if (pixStatus === 'expired') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-yellow-50 border-2 border-[rgba(245,158,11,0.3)] flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-gray-900 font-semibold mb-2">PIX expirado</p>
        <p className="text-sm text-gray-500 mb-4">{mensagemErro || 'O QR Code expirou. Gere um novo para continuar.'}</p>
        <button onClick={onBack} className="text-sm text-emerald-700 hover:text-emerald-700 font-medium transition-colors">
          ← Gerar novo QR Code
        </button>
      </div>
    );
  }

  if (pixStatus === 'rejected' || pixStatus === 'cancelled') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <p className="text-gray-900 font-semibold mb-2">Pagamento não concluído</p>
        <p className="text-sm text-gray-500 mb-4">{mensagemErro || 'Pagamento recusado ou cancelado. Verifique seus dados e tente novamente.'}</p>
        <button onClick={onBack} className="text-sm text-emerald-700 hover:text-emerald-700 font-medium transition-colors">
          ← Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <p className="text-sm font-semibold text-gray-900">Pague via PIX</p>
          <p className="text-xs text-gray-400">{nomeProduto}</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs text-emerald-700 font-medium">Aguardando</span>
        </div>
      </div>

      {/* QR Code */}
      {qrCodeBase64 && (
        <div className="flex justify-center mb-5">
          <div className="p-3 border-2 rounded-2xl shadow-sm bg-white border border-gray-200">
            <img src={`data:image/png;base64,${qrCodeBase64}`} alt="QR Code PIX" className="w-48 h-48 rounded-lg" />
          </div>
        </div>
      )}

      <p className="text-xs text-center text-gray-400 mb-3">ou copie o código abaixo</p>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-3 mb-4">
        <p className="text-xs text-gray-500 flex-1 truncate font-mono">{qrCode}</p>
        <button
          onClick={handleCopy}
          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${ copied ? 'bg-emerald-500 text-white' : ' border text-gray-600 hover:bg-gray-50' }`}
        >
          {copied ? (
            <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Copiado!</>
          ) : (
            <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copiar</>
          )}
        </button>
      </div>

      <p className="text-xs text-center text-gray-400">
        QR Code válido por 24 horas · Acesso liberado automaticamente após o pagamento
      </p>
    </div>
  );
}

// ─── PIX Copy button ──────────────────────────────────────────────────────────

function PixCopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="w-full h-11 rounded-xl text-gray-900 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-150 active:scale-[0.98]"
      style={{ background: copied ? '#16A34A' : '#22C55E' }}
    >
      {copied ? (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          Código copiado!
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copiar código PIX
        </>
      )}
    </button>
  );
}

// ─── Tier por posição na lista de planos ─────────────────────────────────────

type PlanTier = 'basic' | 'recommended' | 'advanced';

function getPlanTier(index: number, total: number): PlanTier {
  if (total === 1) return 'recommended';
  if (total === 2) return index === 0 ? 'basic' : 'recommended';
  // 3+: primeiro = basic, último = advanced, meio = recommended
  if (index === 0) return 'basic';
  if (index === total - 1) return 'advanced';
  return 'recommended';
}

// ─── Metadados por tier ────────────────────────────────────────────────────────

function getPlanoMeta(tier: PlanTier): { subtitle: string; valueItems: string[] } {
  if (tier === 'recommended') {
    return {
      subtitle: 'Ideal para começar a vender no WhatsApp',
      valueItems: [
        'Perfeito para pequenos negócios',
        'Comece a gerar vendas no WhatsApp',
        'Equilíbrio entre custo e resultado',
      ],
    };
  }
  if (tier === 'basic') {
    return {
      subtitle: 'Para começar',
      valueItems: [
        'Uso básico do sistema',
        'Ideal para testes iniciais',
      ],
    };
  }
  return {
    subtitle: 'Para quem já vende todos os dias',
    valueItems: [
      'Alta capacidade de envio',
      'Relatórios de desempenho',
      'Suporte prioritário',
    ],
  };
}

// ─── Card de plano ────────────────────────────────────────────────────────────

function getPlanoDescricao(plano: Plano): string[] {
  const itens: string[] = [
    plano.maxConexoes > 0
      ? `${plano.maxConexoes} conex${plano.maxConexoes === 1 ? 'ão WhatsApp' : 'ões WhatsApp'}`
      : 'Conexões WhatsApp ilimitadas',
    plano.maxDisparos > 0
      ? `${plano.maxDisparos} disparos por dia`
      : 'Disparos diários ilimitados',
  ];

  for (const esp of plano.especificacoes) {
    const texto = esp.trim();
    if (!texto) continue;
    if (itens.some(item => item.toLowerCase() === texto.toLowerCase())) continue;
    itens.push(texto);
  }

  return itens;
}

function getPlanoDescricaoFinal(plano: Plano): string[] {
  const especificacoes = plano.especificacoes.map(esp => esp.trim()).filter(Boolean);
  const temItemDeConexao = especificacoes.some(esp => {
    const texto = esp.toLowerCase();
    return texto.includes('whatsapp') && (
      texto.includes('conecte at') ||
      texto.includes('conta') ||
      texto.includes('numero') ||
      texto.includes('número') ||
      texto.includes('conex')
    );
  });

  const itens: string[] = [];

  if (!temItemDeConexao) {
    itens.push(
      plano.maxConexoes > 0
        ? `${plano.maxConexoes} conex${plano.maxConexoes === 1 ? 'Ã£o WhatsApp' : 'Ãµes WhatsApp'}`
        : 'ConexÃµes WhatsApp ilimitadas'
    );
  }

  itens.push(
    plano.maxDisparos > 0
      ? `${plano.maxDisparos} disparos por dia`
      : 'Disparos diÃ¡rios ilimitados'
  );

  for (const texto of especificacoes) {
    if (itens.some(item => item.toLowerCase() === texto.toLowerCase())) continue;
    itens.push(texto);
  }

  // Insere "Maturador de Chip" automaticamente quando o plano inclui o addon
  if (plano.temMaturador) {
    const jaTemMaturador = itens.some(item => item.toLowerCase().includes('maturador'));
    if (!jaTemMaturador) itens.push('Maturador de Chip incluído');
  }

  return itens;
}

function formatarTempoRestante(expiracao: string | null, diasRestantes: number | null): string {
  if (!expiracao) {
    if ((diasRestantes ?? 0) > 0) {
      return `${diasRestantes} ${diasRestantes === 1 ? 'dia' : 'dias'}`;
    }
    return 'menos de 1 dia';
  }

  const diff = new Date(expiracao).getTime() - Date.now();
  if (diff <= 0) return 'expirado';

  const totalHoras = Math.floor(diff / (1000 * 60 * 60));
  const dias = Math.floor(totalHoras / 24);
  const horas = totalHoras % 24;
  const minutos = Math.max(1, Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)));

  if (dias > 0) {
    return horas > 0
      ? `${dias} ${dias === 1 ? 'dia' : 'dias'} e ${horas}h`
      : `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  }

  if (horas > 0) {
    return `${horas}h e ${minutos}min`;
  }

  return `${minutos} min`;
}

function PlanoCard({ plano, tier, onCheckout }: { plano: Plano; tier: PlanTier; onCheckout: () => void }) {
  const valor = Number(plano.valor);
  const reais = Math.floor(valor);
  const centavos = String(Math.round((valor - reais) * 100)).padStart(2, '0');
  const isRecommended = tier === 'recommended';
  const meta = getPlanoMeta(tier);
  const descricaoItens = getPlanoDescricaoFinal(plano);

  return (
    <div
      className={`relative flex flex-col p-6 ${isRecommended ? 'md:min-h-[455px]' : 'md:min-h-[405px]'}`}
      style={{
        borderRadius: 8,
        border: isRecommended ? '1px solid #d1d5db' : '1px solid #e5e7eb',
        background: '#fff',
        boxShadow: isRecommended
          ? '0 4px 20px rgba(0,0,0,0.08)'
          : '0 1px 4px rgba(0,0,0,0.04)',
        zIndex: isRecommended ? 1 : 0,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      {/* Nome + subtítulo */}
      <div className="mb-1">
        <h3 className="text-sm font-semibold text-gray-900">{plano.nome}</h3>
        <p className="text-xs text-gray-400 mt-0.5">{meta.subtitle}</p>
      </div>

      {/* Badge discreto */}
      {isRecommended && (
        <span className="inline-block mt-2 text-[11px] font-medium text-emerald-700">
          💡 Melhor escolha
        </span>
      )}

      {/* Preço */}
      <div className="mt-2 mb-3">
        <div className="flex items-baseline">
          <span className="text-xs text-gray-400 mr-1">R$</span>
          <span
            className="text-gray-900 leading-none"
            style={{ fontSize: isRecommended ? 32 : 28, fontWeight: 700 }}
          >
            {reais}
          </span>
          <span
            className="text-gray-900 leading-none"
            style={{ fontSize: isRecommended ? 18 : 16, fontWeight: 700 }}
          >
            ,{centavos}
          </span>
          <span className="text-xs text-gray-400 ml-1">/mês</span>
        </div>
      </div>

      {/* Benefícios */}
      <ul className="space-y-1.5 mb-5 flex-1">
        {descricaoItens.map((item) => (
          <li key={item} className="flex items-center gap-2 text-xs text-gray-500">
            <svg
              className="w-3 h-3 flex-shrink-0 text-emerald-500"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            {item}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        onClick={onCheckout}
        className="w-full h-10 rounded-lg font-semibold text-sm transition-colors duration-150 active:scale-[0.98]"
        style={
          isRecommended
            ? { background: '#16a34a', color: '#fff', border: 'none' }
            : tier === 'advanced'
            ? { background: '#fff', color: '#374151', border: '1px solid #d1d5db' }
            : { background: '#fff', color: '#9ca3af', border: '1px solid #e5e7eb' }
        }
        onMouseEnter={e => {
          if (isRecommended) e.currentTarget.style.background = '#15803d';
          else { e.currentTarget.style.background = '#f9fafb'; }
        }}
        onMouseLeave={e => {
          if (isRecommended) e.currentTarget.style.background = '#16a34a';
          else e.currentTarget.style.background = '#fff';
        }}
      >
        {tier === 'basic' ? 'Começar' : tier === 'recommended' ? 'Assinar agora' : 'Assine já'}
      </button>
    </div>
  );
}

// ─── Carregamento do SDK do Mercado Pago ──────────────────────────────────────

function loadMpSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).MercadoPago) { resolve(); return; }
    const existing = document.getElementById('mp-sdk-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = 'mp-sdk-script';
    s.src = 'https://sdk.mercadopago.com/js/v2';
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

// ─── Componente CardBrick (checkout transparente MP) ──────────────────────────

function CardBrick({
  plano, onApproved, onError,
}: {
  plano: Plano;
  onApproved: () => void;
  onError: (msg: string) => void;
}) {
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(false);
  const brickControllerRef = useRef<any>(null);
  const containerId = 'mp-card-brick-container';

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await pagamentosApi.getPublicKey();
        if (cancelled) return;
        if (!data.publicKey) { setSdkError(true); return; }

        await loadMpSdk();
        if (cancelled) return;

        const mp = new (window as any).MercadoPago(data.publicKey, { locale: 'pt-BR' });
        const bricksBuilder = mp.bricks();

        const controller = await bricksBuilder.create('cardPayment', containerId, {
          initialization: {
            amount: Number(plano.valor),
            payer: { email: '' },
          },
          customization: {
            visual: {
              style: {
                theme: 'flat',
                customVariables: {
                  // Cor primária — substitui o roxo padrão do MP
                  baseColor: '#10B981',
                  baseColorFirstVariant: '#059669',
                  baseColorSecondVariant: '#D1FAE5',
                  outlinePrimaryColor: '#10B981',
                  buttonBackground: '#10B981',
                  buttonHoverBackground: '#059669',
                  inputFocusedLabelColor: '#10B981',
                  inputFocusedBorderColor: '#10B981',
                  inputBorderColor: '#E5E7EB',
                  // Tipografia
                  errorColor: '#EF4444',
                  fontFamilyBase: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontSizeMedium: '14px',
                  fontSizeSmall: '11px',
                  fontWeightNormal: '400',
                  fontWeightSemiBold: '500',
                  // Bordas e fundo
                  borderRadiusMedium: '8px',
                  borderRadiusSmall: '6px',
                  borderRadiusFull: '8px',
                  formBackgroundColor: '#FFFFFF',
                  inputBackgroundColor: '#FFFFFF',
                  inputPlaceholderColor: '#D1D5DB',
                  inputFontSize: '14px',
                  labelFontSize: '11px',
                  labelFontWeight: '500',
                  labelColor: '#6B7280',
                },
              },
              hideFormTitle: true,
              defaultPaymentOption: { creditCardForm: true },
            },
            paymentMethods: { minInstallments: 1, maxInstallments: 1 },
          },
          callbacks: {
            onReady: () => { if (!cancelled) setSdkReady(true); },
            onSubmit: async (formData: any) => {
              const payload = {
                token: formData.token,
                payment_method_id: formData.payment_method_id,
                issuer_id: formData.issuer_id,
                installments: formData.installments ?? 1,
                payer: formData.payer,
                planoId: plano.id,
              };
              const { data: result } = await pagamentosApi.criarCartaoDireto(payload);
              if (result.status === 'approved') {
                onApproved();
              } else if (result.statusInterno === 'in_process') {
                // Pagamento em análise — trata como sucesso pendente
                onApproved();
              } else {
                throw new Error(result.mensagemAmigavel || 'Pagamento não aprovado.');
              }
            },
            onError: (err: any) => {
              onError(err?.message || 'Erro no formulário de cartão.');
            },
          },
        });

        if (cancelled) {
          controller.unmount();
        } else {
          brickControllerRef.current = controller;
        }
      } catch {
        if (!cancelled) setSdkError(true);
      }
    })();

    return () => {
      cancelled = true;
      brickControllerRef.current?.unmount();
      brickControllerRef.current = null;
    };
  }, [plano, onApproved, onError]);

  if (sdkError) {
    return (
      <div className="py-4 text-center text-sm text-red-500">
        Não foi possível carregar o formulário de pagamento. Tente recarregar a página.
      </div>
    );
  }

  return (
    <div>
      {/* Faixa de confiança */}
      <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5 mb-4">
        <img src="/img/mercado_pago.svg" alt="Mercado Pago" className="h-5 object-contain flex-shrink-0" />
        <span className="text-xs text-gray-500">Pagamento processado com segurança pelo Mercado Pago</span>
      </div>

      {/* Loader enquanto o Brick não está pronto */}
      {!sdkReady && (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-400">
          <Spinner className="w-4 h-4 text-gray-300" />
          Carregando formulário...
        </div>
      )}

      {/* Container do MP Brick */}
      <div id={containerId} style={{ display: sdkReady ? 'block' : 'none' }} />

      {sdkReady && (
        <p className="text-[11px] text-gray-400 text-center mt-3">
          Seus dados de pagamento são enviados com segurança ao Mercado Pago.
        </p>
      )}
    </div>
  );
}

// ─── Bandeiras de pagamento aceitas pelo Mercado Pago ─────────────────────────

function PaymentBrands() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Visa */}
      <div className="h-7 px-2.5 bg-[#1A1F71] rounded-md flex items-center justify-center">
        <span className="text-gray-900 font-black text-sm italic tracking-tighter">VISA</span>
      </div>
      {/* Mastercard */}
      <div className="h-7 w-12 bg-gray-900 rounded-md flex items-center justify-center relative overflow-hidden">
        <div className="absolute left-1.5 w-4 h-4 rounded-full bg-[#EB001B]" />
        <div className="absolute right-1.5 w-4 h-4 rounded-full bg-[#F79E1B] opacity-90" />
      </div>
      {/* Elo */}
      <div className="h-7 px-2.5 bg-[#FFD500] rounded-md flex items-center justify-center">
        <span className="text-gray-900 font-black text-xs tracking-tight">elo</span>
      </div>
      {/* Hipercard */}
      <div className="h-7 px-2.5 rounded-md flex items-center justify-center" style={{ background: '#B30019' }}>
        <span className="text-gray-900 font-bold text-[10px] tracking-tight">Hiper</span>
      </div>
      {/* Amex */}
      <div className="h-7 px-2.5 bg-[#007BC1] rounded-md flex items-center justify-center">
        <span className="text-gray-900 font-bold text-[10px] tracking-tight">AMEX</span>
      </div>
      {/* Diners */}
      <div className="h-7 px-2.5 bg-[#004B87] rounded-md flex items-center justify-center">
        <span className="text-gray-900 font-bold text-[10px]">Diners</span>
      </div>
      {/* PIX */}
      <div className="h-7 px-2.5 bg-[#32BCAD] rounded-md flex items-center justify-center gap-1">
        <svg viewBox="0 0 512 512" fill="white" className="w-3 h-3">
          <path d="M242.4 292.5C247.8 287.1 255.1 284.1 262.5 284.1C269.9 284.1 277.2 287 282.6 292.5L350.2 360C361.5 371.3 375.6 377.2 390.1 377.2C404.5 377.2 418.6 371.3 429.9 360C451.7 338.2 451.7 303.1 429.9 281.2L361.1 212.4C356.1 207.4 352.7 200.7 352.7 194C352.7 187.3 355.7 180.6 361.1 175.6L429.9 106.9C451.7 85.1 451.7 50 429.9 28.2C418.6 17 404.5 11.1 390.1 11.1C375.6 11.1 361.5 17 350.2 28.2L282.6 95.9C277.2 101.4 269.9 104.3 262.5 104.3C255.1 104.3 247.8 101.4 242.4 95.9L174.8 28.2C163.5 17 149.4 11.1 134.9 11.1C120.5 11.1 106.4 17 95.1 28.2C73.3 50 73.3 85.1 95.1 106.9L163.8 175.6C169.2 181 172.2 187.7 172.2 194.4C172.2 201.1 169.2 207.8 163.8 213.2L95.1 281.9C73.3 303.7 73.3 338.8 95.1 360.6C106.4 371.8 120.5 377.7 134.9 377.7C149.4 377.7 163.5 371.8 174.8 360.6L242.4 292.5z" />
        </svg>
        <span className="text-gray-900 font-bold text-[10px]">PIX</span>
      </div>
      {/* Boleto */}
      <div className="h-7 px-2.5 bg-gray-100 border border-gray-200 rounded-md flex items-center justify-center">
        <span className="text-gray-600 font-bold text-[10px]">Boleto</span>
      </div>
    </div>
  );
}

// ─── Ícone oficial do PIX ─────────────────────────────────────────────────────

function PixLogo({ className = 'w-6 h-6', color = '#32BCAD' }: { className?: string; color?: string }) {
  return (
    <svg viewBox="0 0 512 512" fill={color} className={className}>
      <path d="M242.4 292.5C247.8 287.1 255.1 284.1 262.5 284.1C269.9 284.1 277.2 287 282.6 292.5L350.2 360C361.5 371.3 375.6 377.2 390.1 377.2C404.5 377.2 418.6 371.3 429.9 360C451.7 338.2 451.7 303.1 429.9 281.2L361.1 212.4C356.1 207.4 352.7 200.7 352.7 194C352.7 187.3 355.7 180.6 361.1 175.6L429.9 106.9C451.7 85.1 451.7 50 429.9 28.2C418.6 17 404.5 11.1 390.1 11.1C375.6 11.1 361.5 17 350.2 28.2L282.6 95.9C277.2 101.4 269.9 104.3 262.5 104.3C255.1 104.3 247.8 101.4 242.4 95.9L174.8 28.2C163.5 17 149.4 11.1 134.9 11.1C120.5 11.1 106.4 17 95.1 28.2C73.3 50 73.3 85.1 95.1 106.9L163.8 175.6C169.2 181 172.2 187.7 172.2 194.4C172.2 201.1 169.2 207.8 163.8 213.2L95.1 281.9C73.3 303.7 73.3 338.8 95.1 360.6C106.4 371.8 120.5 377.7 134.9 377.7C149.4 377.7 163.5 371.8 174.8 360.6L242.4 292.5z" />
    </svg>
  );
}

// ─── Ícone de cartão ──────────────────────────────────────────────────────────

function CardIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}

// ─── Bandeiras de cartão (mini, inline) ───────────────────────────────────────

function CardBrandsInline() {
  return (
    <img src="/img/processout.svg" alt="Cartões aceitos" className="h-5 object-contain" />
  );
}

// ─── Página principal de pagamento ────────────────────────────────────────────

export default function PagamentoPage({ nomeCompleto, subPage, onSubscriptionActive, onLogout, onVoltar }: Props) {
  const [method, setMethod] = useState<Method>('pix');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [selectedPlanoId, setSelectedPlanoId] = useState<number | null>(null);
  const [nomeProduto, setNomeProduto] = useState('');
  const [cpf, setCpf] = useState('');
  const [pixData, setPixData] = useState<{ paymentId: string; qrCode: string; qrCodeBase64: string } | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [trialInfo, setTrialInfo] = useState<{ modoTeste: boolean; diasRestantes: number | null; ativo: boolean; expiracao: string | null } | null>(null);
  const [showTrialPopup, setShowTrialPopup] = useState(false);

  useEffect(() => {
    planosApi.list()
      .then(({ data }) => {
        setPlanos(data);
        const destaque = data.find(p => p.destaque) ?? data[0];
        if (destaque) setSelectedPlanoId(destaque.id);
      })
      .catch(() => {});

    pagamentosApi.getInfoPlano()
      .then(({ data }) => setNomeProduto(data.nomeProduto))
      .catch(() => {});

    pagamentosApi.verificarAssinatura()
      .then(({ data }) => {
        setTrialInfo({ modoTeste: data.modoTeste, diasRestantes: data.diasRestantes, ativo: data.ativo, expiracao: data.expiracao });
        if (data.modoTeste && (!data.ativo || (data.diasRestantes ?? 1) <= 0)) {
          setShowTrialPopup(true);
        }
      })
      .catch(() => {});
  }, []);

  const handlePixApproved = useCallback(() => {
    pagamentosApi.verificarAssinatura()
      .then(({ data }) => { if (data.ativo && data.expiracao) onSubscriptionActive(data.expiracao); })
      .catch(() => {});
  }, [onSubscriptionActive]);

  // Polling do PIX inline (quando QR está visível no checkout)
  useEffect(() => {
    if (!pixData || !checkoutOpen) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await pagamentosApi.consultarStatus(pixData.paymentId);
        if (data.status === 'approved') {
          clearInterval(iv);
          handlePixApproved();
        } else if (['rejected', 'cancelled', 'refunded', 'charged_back'].includes(data.status)) {
          clearInterval(iv);
          setPixData(null);
          setError('Pagamento não concluído. Tente novamente.');
        }
      } catch {}
    }, 4000);
    return () => clearInterval(iv);
  }, [pixData, checkoutOpen, handlePixApproved]);

  if (subPage === 'sucesso') return <SuccessPage onSubscriptionActive={onSubscriptionActive} />;
  if (subPage === 'falha') return <FailurePage />;
  if (subPage === 'pendente') return <PendingPage />;

  const selectedPlano = planos.find(p => p.id === selectedPlanoId);

  const validarCPFLocal = (cpfStr: string): boolean => {
    const c = cpfStr.replace(/\D/g, '');
    if (c.length !== 11 || /^(.)\1+$/.test(c)) return false;
    const calc = (len: number) =>
      c.slice(0, len).split('').reduce((sum: number, d: string, i: number) => sum + +d * (len + 1 - i), 0);
    const mod11 = (n: number) => ((n * 10) % 11) % 10;
    return mod11(calc(9)) === +c[9] && mod11(calc(10)) === +c[10];
  };

  const handlePay = async () => {
    setError('');

    // Validação local de CPF para PIX (backend valida novamente — isso é só para UX)
    if (method === 'pix') {
      const cpfLimpo = cpf.replace(/\D/g, '');
      if (!cpfLimpo) {
        setError('CPF é obrigatório para pagamento PIX.');
        return;
      }
      if (!validarCPFLocal(cpfLimpo)) {
        setError('CPF inválido. Verifique o número informado.');
        return;
      }
    }

    setLoading(true);
    try {
      if (method === 'pix') {
        const { data } = await pagamentosApi.criarPix(selectedPlanoId ?? undefined, cpf || undefined);
        setPixData({ paymentId: data.paymentId, qrCode: data.qrCode, qrCodeBase64: data.qrCodeBase64 });
      } else {
        const { data } = await pagamentosApi.criarCartao(method, selectedPlanoId ?? undefined);
        window.location.href = data.initPoint;
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      if (msg) {
        setError(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg[0] : 'Erro ao gerar pagamento.');
      } else {
        setError('Não foi possível gerar o pagamento. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const firstName = nomeCompleto.split(' ')[0].toUpperCase();

  const gridCols =
    planos.length <= 1 ? 'grid-cols-1 max-w-sm' :
    planos.length === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl' :
    planos.length === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl' :
    'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl';

  // Header de marca reutilizável
  const BrandHeader = () => (
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md">
        <svg className="w-5 h-5 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <span className="text-gray-900 font-bold text-xl">{nomeProduto}</span>
    </div>
  );

  // ── Tela de Checkout ─────────────────────────────────────────────────────────
  if (checkoutOpen && selectedPlano) {
    const preco = Number(selectedPlano.valor).toFixed(2).replace('.', ',');
    const isPix = method === 'pix';
    const hasQR = !!pixData;

    return (
      <div className="min-h-screen py-10 px-4" style={{ background: '#F8FAFC' }}>
        <div className="mx-auto" style={{ maxWidth: 980 }}>

          {/* Top bar */}
          <div className="flex items-center justify-end mb-10">
            <button
              onClick={() => { setCheckoutOpen(false); setError(''); setPixData(null); }}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Voltar aos planos
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-7 items-start">

            {/* ══ COLUNA ESQUERDA — métodos ══ */}
            <div className="rounded-2xl border overflow-hidden bg-white border-gray-100">

              <p className="text-xs font-semibold text-gray-400 px-5 pt-5 pb-3 uppercase tracking-wider">Escolha um método de pagamento</p>

                {/* ── PIX (destaque) ── */}
                <div
                  className="mx-4 mb-2 rounded-xl overflow-hidden transition-all duration-200 bg-white border border-gray-200"
                >

                  {/* Header PIX */}
                  <div
                    className="flex items-center justify-between px-4 py-3.5 cursor-pointer"
                    onClick={() => { if (!hasQR) setMethod('pix'); }}
                  >
                    <div className="flex items-center gap-3">
                      {/* Radio */}
                      <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
                        style={{ borderColor: isPix ? '#22C55E' : '#D1D5DB' }}>
                        {isPix && <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-gray-900">PIX</span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                          Recomendado
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <img src="/img/pix.png" alt="PIX" className="w-20 h-20 object-contain" />
                      <svg className={`w-4 h-4 transition-transform text-gray-400 ${isPix ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Corpo PIX expandido */}
                  {isPix && (
                    <div
                      className="px-4 pb-5"
                      style={{ borderTop: '1px solid #F3F4F6' }}
                      onClick={e => e.stopPropagation()}
                    >
                      {hasQR ? (
                        /* QR Code inline */
                        <div className="pt-4 space-y-3">
                          {pixData!.qrCodeBase64 && (
                            <div className="flex justify-center">
                              <div className="p-2.5 border rounded-xl shadow-sm bg-white border border-gray-200">
                                <img
                                  src={`data:image/png;base64,${pixData!.qrCodeBase64}`}
                                  alt="QR Code PIX"
                                  className="w-44 h-44 rounded-lg"
                                />
                              </div>
                            </div>
                          )}
                          <div className="flex items-center justify-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-xs font-medium text-emerald-700">Aguardando pagamento...</span>
                          </div>
                          <PixCopyButton code={pixData!.qrCode} />
                          <p className="text-[11px] text-gray-400 text-center">QR Code válido por 24 horas · Acesso liberado automaticamente</p>
                          <div className="text-center">
                            <button onClick={() => setPixData(null)} className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors underline">
                              Gerar novo QR Code
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* CPF input + botão PIX */
                        <div className="pt-5 space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1.5">CPF do titular</label>
                            <input
                              type="text"
                              value={cpf}
                              onChange={e => {
                                const v = e.target.value.replace(/\D/g, '').slice(0, 11);
                                const fmt = v.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                                setCpf(fmt);
                              }}
                              placeholder="000.000.000-00"
                              className="field-input w-full h-10 border rounded-lg px-3.5 text-sm text-gray-900 placeholder-gray-400 bg-white border border-gray-200"
                            />
                          </div>

                          {/* Botão PIX — abaixo do campo de CPF */}
                          <div className="space-y-2">
                            <button
                              onClick={handlePay}
                              disabled={loading}
                              className="w-full disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 flex items-center justify-center gap-2"
                              style={{
                                height: 46,
                                fontSize: 14,
                                fontWeight: 600,
                                borderRadius: 8,
                                background: '#22C55E',
                                transition: 'background 0.2s, transform 0.2s',
                              }}
                              onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = '#16A34A'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                              onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = '#22C55E'; e.currentTarget.style.transform = 'translateY(0)'; } }}
                              onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                              onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            >
                              {loading ? <><Spinner className="w-4 h-4" />Gerando PIX...</> : 'Finalizar pagamento com PIX'}
                            </button>
                            <p className="text-center text-[11px]" style={{ color: '#9CA3AF' }}>
                              Leva menos de 30 segundos para concluir
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Cartão (secundário) ── */}
                <div
                  className="mx-4 mb-4 rounded-xl overflow-hidden transition-all duration-200 cursor-pointer bg-white border border-gray-200"
                  onClick={() => { if (!hasQR) setMethod('credit_card'); }}
                >
                  <div className="flex items-center justify-between px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
                        style={{ borderColor: !isPix ? '#22C55E' : '#D1D5DB' }}>
                        {!isPix && <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />}
                      </div>
                      <CardIcon className="w-4 h-4 text-gray-300" />
                      <div>
                        <span className="text-sm text-gray-500">Cartão de crédito ou débito</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <CardBrandsInline />
                      <svg className={`w-4 h-4 transition-transform text-gray-400 ${!isPix ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  {!isPix && (
                    <div className="px-4 pb-5 pt-3 border-t border-gray-100" onClick={e => e.stopPropagation()}>
                      <CardBrick
                        plano={selectedPlano}
                        onApproved={handlePixApproved}
                        onError={msg => setError(msg)}
                      />
                    </div>
                  )}
                </div>

            </div>

            {/* ══ COLUNA DIREITA — resumo + CTA ══ */}
            <div className="lg:sticky lg:top-8 space-y-4">

              {/* Card resumo */}
              <div className="rounded-2xl border p-6 space-y-6"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>

                <div className="space-y-5">
                  <p className="text-xs font-medium uppercase tracking-widest text-gray-400">Resumo do pedido</p>

                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Plano:</span>
                    <span className="text-sm font-semibold text-gray-900">{selectedPlano.nome}</span>
                  </div>

                  <div className="flex items-center justify-between pt-5 border-t border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Total mensal</span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-lg font-semibold text-gray-900">R$ {preco}</span>
                      <span className="text-xs text-gray-400 font-normal">/ mês</span>
                    </div>
                  </div>
                </div>

                {/* Erro */}
                {error && (
                  <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs text-red-500">{error}</p>
                  </div>
                )}


                {/* Aguardando PIX */}
                {hasQR && (
                  <div className="flex items-center justify-center gap-2 py-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-sm font-semibold text-emerald-700">Aguardando confirmação do PIX...</span>
                  </div>
                )}

                {/* Selos de confiança */}
                <div className="space-y-2.5 pt-1 border-t border-gray-100">
                  {[
                    { d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', text: 'Pagamento seguro via Mercado Pago' },
                    { d: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z', text: 'Seus dados são criptografados' },
                    { d: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', text: 'Liberação após confirmação do pagamento' },
                  ].map(({ d, text }) => (
                    <div key={text} className="flex items-center gap-2 text-xs text-gray-400">
                      <svg className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
                      </svg>
                      {text}
                    </div>
                  ))}
                </div>

              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Tela de Planos ────────────────────────────────────────────────────────────
  return (
    <div className="h-screen overflow-y-auto px-4 py-8 flex flex-col" style={{ background: '#f5f5f5' }}>

      {/* ── Popup: Teste expirado ── */}
      {showTrialPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.45)' }}
        >
          <div className="rounded-xl shadow-xl max-w-sm w-full p-7 text-center bg-white border border-gray-200">
            <div className="w-14 h-14 rounded-full bg-yellow-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Seu período gratuito acabou</h2>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Para continuar usando o disparador e não perder seus contatos, escolha um plano abaixo.
            </p>
            <button
              onClick={() => setShowTrialPopup(false)}
              className="w-full h-11 rounded-lg text-sm font-semibold text-gray-900 transition-colors"
              style={{ background: '#16a34a' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#15803d'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#16a34a'; }}
            >
              Escolher plano
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto w-full flex flex-col" style={{ maxWidth: 1100 }}>

        {/* Cabeçalho */}
        <div className="text-center mb-5 relative">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-3">
            <button
              onClick={onVoltar ?? onLogout}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Voltar
            </button>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            Comece a vender mais pelo WhatsApp hoje
          </h2>
        </div>

        {/* Banner de trial ativo */}
        {trialInfo?.modoTeste && trialInfo.ativo && (trialInfo.diasRestantes ?? 0) > 0 && (
          <div className="text-center mb-5">
            <p className="text-sm font-medium text-yellow-700">
              ⚠️ Seu teste expira em <span className="font-bold">{formatarTempoRestante(trialInfo.expiracao, trialInfo.diasRestantes)}</span>
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Assine para continuar usando</p>
          </div>
        )}

        {/* Grid de Planos */}
        {planos.length > 0 && (
          <div
            className={`grid gap-5 mx-auto items-end ${gridCols}`}
            style={{ paddingTop: 12, paddingBottom: 8 }}
          >
            {planos.map((plano, i) => (
              <PlanoCard
                key={plano.id}
                plano={plano}
                tier={getPlanTier(i, planos.length)}
                onCheckout={() => { setSelectedPlanoId(plano.id); setCheckoutOpen(true); setError(''); }}
              />
            ))}
          </div>
        )}

        {/* Trust strip */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-2 pt-3 border-t border-gray-200">
          <span className="text-xs text-gray-400">🔒 Dados protegidos</span>
          <span className="text-gray-400 select-none">·</span>
          <span className="text-xs text-gray-400">💳 Pagamento via Mercado Pago</span>
          <span className="text-gray-400 select-none">·</span>
          <img src="/img/mercado_pago.svg" alt="Mercado Pago" style={{ height: 32, opacity: 0.6 }} />
        </div>

        <div className="text-center mt-4">
          <p className="text-xs text-gray-400">
            © 2026 Disparador Pro. Todos os direitos reservados.
          </p>
        </div>

      </div>
    </div>
  );
}
